import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountBalance as CaixaIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  GridOn as ExcelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, isAfter, isBefore, isSameDay, parseISO, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { pdf } from '@react-pdf/renderer';
import {
  MovimentacaoCaixa,
  TipoMovimentacaoCaixa,
  CATEGORIAS_ENTRADA_CAIXA,
  CATEGORIAS_SAIDA_CAIXA,
  CATEGORIA_ENTRADA_LABEL,
  CATEGORIA_SAIDA_LABEL,
  CategoriaEntradaCaixa,
  CategoriaSaidaCaixa,
  movimentacaoAfetaSaldoCaixa,
} from '../../types';
import { cashMovementService } from '../../services/caixaService';
import MovimentacoesReportPDF from './MovimentacoesReportPDF';
import { exportMovimentacoesReportExcel } from '../../utils/reportExcelExports';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

/** Máscara pt-BR: R$ X.XXX,XX */
const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const getCategoryLabel = (m: MovimentacaoCaixa): string => {
  if (!m.category) return '—';
  if (m.type === 'entrada') return CATEGORIA_ENTRADA_LABEL[m.category as CategoriaEntradaCaixa] ?? m.category;
  return CATEGORIA_SAIDA_LABEL[m.category as CategoriaSaidaCaixa] ?? m.category;
};

type ListFilter = 'all' | 'payable_pending' | 'settled';

const kpiCardSx = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
};

const MovimentacoesList: React.FC = () => {
  const navigate = useNavigate();
  const [movimentacoes, setMovimentacoes] = useState<MovimentacaoCaixa[]>([]);
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [periodStart, setPeriodStart] = useState<string>(''); // YYYY-MM-DD
  const [periodEnd, setPeriodEnd] = useState<string>(''); // YYYY-MM-DD
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [form, setForm] = useState<{
    type: TipoMovimentacaoCaixa;
    category: string;
    amount: number;
    description: string;
    date: string;
    isPayable: boolean;
    dueDate: string;
    payableNote: string;
  }>({
    type: 'entrada',
    category: CATEGORIAS_ENTRADA_CAIXA[0].value,
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
    isPayable: false,
    dueDate: new Date().toISOString().split('T')[0],
    payableNote: '',
  });

  const loadList = useCallback(async () => {
    const data = await cashMovementService.getAll();
    setMovimentacoes(data);
  }, []);

  useEffect(() => {
    loadList().catch(() => setSnackbar('Não foi possível carregar as movimentações.'));
  }, [loadList]);

  const urgentPayables = useMemo(() => {
    const todayStart = startOfDay(new Date());
    return movimentacoes.filter((m) => {
      if (m.type !== 'saida' || m.payableStatus !== 'pending' || !m.dueDate) return false;
      let d: Date;
      try {
        d = startOfDay(parseISO(m.dueDate));
      } catch {
        return false;
      }
      return !isAfter(d, todayStart);
    });
  }, [movimentacoes]);

  const filteredRows = useMemo(() => {
    const list = [...movimentacoes].sort(
      (a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime()
    );
    const withPeriod = list.filter((m) => {
      if (!periodStart && !periodEnd) return true;
      const d = m.date ? new Date(`${String(m.date).split('T')[0]}T12:00:00`) : null;
      if (!d || Number.isNaN(d.getTime())) return false;
      const start = periodStart ? new Date(`${periodStart}T00:00:00`) : null;
      const end = periodEnd ? new Date(`${periodEnd}T23:59:59`) : null;
      if (start && !Number.isNaN(start.getTime()) && d < start) return false;
      if (end && !Number.isNaN(end.getTime()) && d > end) return false;
      return true;
    });

    if (listFilter === 'payable_pending') {
      return withPeriod.filter((m) => m.type === 'saida' && m.payableStatus === 'pending');
    }
    if (listFilter === 'settled') {
      return withPeriod.filter((m) => !(m.type === 'saida' && m.payableStatus === 'pending'));
    }
    return withPeriod;
  }, [movimentacoes, listFilter, periodStart, periodEnd]);

  const totals = useMemo(() => {
    const totalEntradas = filteredRows
      .filter((m) => m.type === 'entrada')
      .reduce((sum, m) => sum + toNum(m.amount), 0);
    const totalSaidasAll = filteredRows
      .filter((m) => m.type === 'saida')
      .reduce((sum, m) => sum + toNum(m.amount), 0);
    const totalSaidasDebitado = filteredRows
      .filter((m) => m.type === 'saida' && movimentacaoAfetaSaldoCaixa(m))
      .reduce((sum, m) => sum + toNum(m.amount), 0);
    const totalSaidasPendentes = filteredRows
      .filter((m) => m.type === 'saida' && !movimentacaoAfetaSaldoCaixa(m))
      .reduce((sum, m) => sum + toNum(m.amount), 0);
    return { totalEntradas, totalSaidasAll, totalSaidasDebitado, totalSaidasPendentes };
  }, [filteredRows]);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const subtitle =
        listFilter === 'all'
          ? 'Filtro: Todas'
          : listFilter === 'payable_pending'
            ? 'Filtro: Contas a pagar (pendentes)'
            : 'Filtro: Quitadas / entradas';
      const periodLabel =
        periodStart || periodEnd
          ? `Período: ${periodStart ? format(new Date(`${periodStart}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR }) : '—'} até ${periodEnd ? format(new Date(`${periodEnd}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR }) : '—'}`
          : null;

      const base = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
      const logoUrl = `${base}/assets/logos/${encodeURIComponent('MARCA - R MOTORS horizontal.png')}`;

      const doc = (
        <MovimentacoesReportPDF
          movimentacoes={filteredRows}
          generatedAtLabel={generatedAtLabel}
          logoUrl={logoUrl}
          title="Relatório de movimentações"
          subtitle={periodLabel ? `${subtitle} · ${periodLabel}` : subtitle}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `movimentacoes-${now.toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar('Não foi possível gerar o PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const subtitle =
        listFilter === 'all'
          ? 'Filtro: Todas'
          : listFilter === 'payable_pending'
            ? 'Filtro: Contas a pagar (pendentes)'
            : 'Filtro: Quitadas / entradas';
      const periodLabel =
        periodStart || periodEnd
          ? `Período: ${periodStart ? format(new Date(`${periodStart}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR }) : '—'} até ${periodEnd ? format(new Date(`${periodEnd}T00:00:00`), 'dd/MM/yyyy', { locale: ptBR }) : '—'}`
          : null;

      await exportMovimentacoesReportExcel({
        movimentacoes: filteredRows,
        generatedAtLabel,
        title: 'Relatório de movimentações',
        subtitle: periodLabel ? `${subtitle} · ${periodLabel}` : subtitle,
        filename: `movimentacoes-${now.toISOString().slice(0, 10)}.xlsx`,
      });
    } catch {
      setSnackbar('Não foi possível gerar o Excel.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleOpenDialog = () => {
    const d = new Date().toISOString().split('T')[0];
    setForm({
      type: 'entrada',
      category: CATEGORIAS_ENTRADA_CAIXA[0].value,
      amount: 0,
      description: '',
      date: d,
      isPayable: false,
      dueDate: d,
      payableNote: '',
    });
    setDialogOpen(true);
  };

  const handleTypeChange = (type: TipoMovimentacaoCaixa) => {
    const category = type === 'entrada' ? CATEGORIAS_ENTRADA_CAIXA[0].value : CATEGORIAS_SAIDA_CAIXA[0].value;
    setForm((prev) => ({
      ...prev,
      type,
      category,
      isPayable: type === 'entrada' ? false : prev.isPayable,
    }));
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    const amount = toNum(form.amount);
    if (amount <= 0 || !form.description.trim()) return;
    if (form.type === 'saida' && form.isPayable && !form.dueDate) {
      setSnackbar('Informe a data de vencimento da conta a pagar.');
      return;
    }
    setSaving(true);
    try {
      const base = {
        type: form.type,
        category: form.category as CategoriaEntradaCaixa | CategoriaSaidaCaixa,
        amount,
        description: form.description.trim(),
        date: form.date,
      };
      if (form.type === 'entrada') {
        await cashMovementService.create(base);
      } else if (form.isPayable) {
        await cashMovementService.create({
          ...base,
          payableStatus: 'pending',
          dueDate: form.dueDate,
          payableNote: form.payableNote.trim() || undefined,
        });
      } else {
        await cashMovementService.create({
          ...base,
          payableStatus: 'paid',
        });
      }
      handleCloseDialog();
      await loadList();
    } catch {
      setSnackbar('Não foi possível salvar a movimentação.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm('Excluir esta movimentação?')) return;
    try {
      await cashMovementService.delete(id);
      await loadList();
    } catch {
      setSnackbar('Não foi possível excluir.');
    }
  };

  const handleSettle = async (m: MovimentacaoCaixa) => {
    if (
      !window.confirm(
        'Registrar o pagamento hoje e debitar este valor no caixa? A data da movimentação será atualizada para hoje.'
      )
    ) {
      return;
    }
    try {
      await cashMovementService.update(m.id, {
        payableStatus: 'paid',
        date: new Date().toISOString().split('T')[0],
      });
      await loadList();
    } catch {
      setSnackbar('Não foi possível dar baixa na conta.');
    }
  };

  const dueLabel = (m: MovimentacaoCaixa): string | null => {
    if (!m.dueDate || m.payableStatus !== 'pending') return null;
    try {
      const d = startOfDay(parseISO(m.dueDate));
      const todayStart = startOfDay(new Date());
      if (isBefore(d, todayStart)) return 'Atrasado';
      if (isSameDay(d, todayStart)) return 'Vence hoje';
    } catch {
      return null;
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Movimentações
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>
            Registro formal de entradas e saídas. Saídas marcadas como conta a pagar ficam pendentes e só debitam o caixa após a baixa.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void loadList()} disabled={false}>
            Atualizar
          </Button>
          <Button variant="outlined" startIcon={<CaixaIcon />} onClick={() => navigate('/caixa')}>
            Caixa
          </Button>
          <Button variant="outlined" onClick={() => navigate('/movimentacoes/recorrentes')}>
            Recorrentes
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<ExcelIcon />}
            onClick={() => void handleDownloadExcel()}
            disabled={downloadingExcel || filteredRows.length === 0}
          >
            {downloadingExcel ? 'Gerando Excel...' : 'Baixar Excel'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => void handleDownloadPdf()}
            disabled={downloadingPdf || filteredRows.length === 0}
          >
            {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Nova movimentação
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Typography variant="overline" color="text.secondary">
              Entradas (período)
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {formatCurrency(totals.totalEntradas)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Soma das movimentações tipo entrada.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Typography variant="overline" color="text.secondary">
              Gastos (saídas)
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {formatCurrency(totals.totalSaidasAll)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Soma de todas as saídas (debitadas + pendentes).
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Typography variant="overline" color="text.secondary">
              Saídas debitadas
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {formatCurrency(totals.totalSaidasDebitado)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Saídas que afetam o caixa (pagas).
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={0} sx={kpiCardSx}>
            <Typography variant="overline" color="text.secondary">
              Saídas pendentes
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
              {formatCurrency(totals.totalSaidasPendentes)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Contas a pagar (não debitam o caixa ainda).
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {urgentPayables.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Contas a pagar — vencimento ou atraso
          </Typography>
          <Stack spacing={0.75}>
            {urgentPayables.map((m) => {
              const label = dueLabel(m);
              const msg = (m.payableNote || m.description || '').trim();
              return (
                <Typography key={m.id} variant="body2">
                  <strong>{formatCurrency(m.amount)}</strong>
                  {m.dueDate && (
                    <>
                      {' '}
                      · venc. {format(parseISO(m.dueDate), 'dd/MM/yyyy', { locale: ptBR })}
                    </>
                  )}
                  {label && ` · ${label}`}
                  {msg && ` — ${msg}`}
                </Typography>
              );
            })}
          </Stack>
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Filtros:
              </Typography>
              <ToggleButtonGroup
                size="small"
                value={listFilter}
                exclusive
                onChange={(_, v: ListFilter | null) => v && setListFilter(v)}
              >
                <ToggleButton value="all">Todas</ToggleButton>
                <ToggleButton value="payable_pending">Pendentes</ToggleButton>
                <ToggleButton value="settled">Quitadas/Entradas</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="De"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="Até"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              {(periodStart || periodEnd) && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    setPeriodStart('');
                    setPeriodEnd('');
                  }}
                >
                  Limpar período
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Data</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Caixa</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Descrição / lembrete</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="center" width={100}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma movimentação neste filtro. Use &quot;Nova movimentação&quot; para registrar entradas ou
                      saídas.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((m) => {
                  const pendingPayable = m.type === 'saida' && m.payableStatus === 'pending';
                  const affects = movimentacaoAfetaSaldoCaixa(m);
                  const dl = dueLabel(m);
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>
                        {m.date ? format(parseISO(m.date), 'dd/MM/yyyy', { locale: ptBR }) : '—'}
                      </TableCell>
                      <TableCell>
                        {m.dueDate
                          ? format(parseISO(m.dueDate), 'dd/MM/yyyy', { locale: ptBR })
                          : '—'}
                        {dl && (
                          <Chip size="small" label={dl} color={dl === 'Atrasado' ? 'error' : 'warning'} sx={{ ml: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={m.type === 'entrada' ? 'Entrada' : 'Saída'}
                          color={m.type === 'entrada' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {m.type === 'entrada' ? (
                          <Chip size="small" label="Entra no caixa" color="success" variant="outlined" />
                        ) : affects ? (
                          <Chip size="small" label="Debitado" color="default" variant="outlined" />
                        ) : (
                          <Chip size="small" label="Pendente" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>{getCategoryLabel(m)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{m.description}</Typography>
                        {m.payableNote && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Lembrete: {m.payableNote}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {m.type === 'entrada' ? '+' : '−'} {formatCurrency(m.amount)}
                      </TableCell>
                      <TableCell align="center">
                        {pendingPayable && (
                          <Tooltip title="Registrar pagamento e debitar o caixa">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleSettle(m)}
                              aria-label="Dar baixa no caixa"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(m.id)}
                          aria-label="Excluir"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Nova movimentação</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={form.type}
                label="Tipo"
                onChange={(e) => handleTypeChange(e.target.value as TipoMovimentacaoCaixa)}
              >
                <MenuItem value="entrada">Entrada</MenuItem>
                <MenuItem value="saida">Saída</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={form.category}
                label="Categoria"
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {(form.type === 'entrada' ? CATEGORIAS_ENTRADA_CAIXA : CATEGORIAS_SAIDA_CAIXA).map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Valor (R$)"
              value={formatCurrency(form.amount)}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, '');
                setForm({ ...form, amount: v === '' ? 0 : parseFloat(v) / 100 });
              }}
              placeholder="R$ 0,00"
            />
            <TextField
              fullWidth
              size="small"
              label="Data"
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value, dueDate: form.isPayable ? e.target.value : form.dueDate })}
              InputLabelProps={{ shrink: true }}
              helperText="Data de competência ou do pagamento (conforme o caso)"
            />
            {form.type === 'saida' && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.isPayable}
                      onChange={(_, checked) =>
                        setForm((prev) => ({
                          ...prev,
                          isPayable: checked,
                          dueDate: checked ? prev.date : prev.dueDate,
                        }))
                      }
                    />
                  }
                  label="Conta a pagar (não debita o caixa até dar baixa no pagamento)"
                />
                {form.isPayable && (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="Data de vencimento"
                      type="date"
                      value={form.dueDate}
                      onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Lembrete ao vencer (opcional)"
                      value={form.payableNote}
                      onChange={(e) => setForm({ ...form, payableNote: e.target.value })}
                      placeholder="Ex.: pagar fornecedor X, boleto linha Y"
                      multiline
                      rows={2}
                    />
                  </>
                )}
              </>
            )}
            <TextField
              fullWidth
              size="small"
              label="Descrição"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!form.description.trim() || form.amount <= 0 || saving}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)}>
        <Alert severity="error" variant="filled" onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MovimentacoesList;
